import { useState, type ImgHTMLAttributes } from "react";

interface OptimizedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

const LOCAL_RASTER = /^\/images\/.+\.(jpe?g|png)$/i;

// Every local /images/*.jpg has pre-generated .avif/.webp siblings (see
// scripts run for the image-optimization pass — public/images/courses/*).
//
// This deliberately uses a plain <img> with an onError fallback chain
// instead of <picture>/<source> — confirmed live 2026-09-21 that a
// deployment can end up with .avif files missing (uploaded/synced
// incompletely) while .jpg/.webp exist. <picture> commits to the first
// <source> whose *type* the browser supports and never retries the next
// source when that specific file 404s, so a single missing avif broke
// every course image for any AVIF-capable browser even though a working
// webp/jpg sat right next to it. onError-based fallback degrades
// gracefully regardless of which specific files actually made it to a
// given server, at the cost of an extra request only in the failure case.
export default function OptimizedImage({ src, alt, ...imgProps }: OptimizedImageProps) {
  const isLocal = LOCAL_RASTER.test(src);
  const base = isLocal ? src.replace(/\.(jpe?g|png)$/i, "") : null;
  const [stage, setStage] = useState<"avif" | "webp" | "original">("avif");

  if (!isLocal || !base) {
    return <img src={src} alt={alt} {...imgProps} />;
  }

  const currentSrc = stage === "avif" ? `${base}.avif` : stage === "webp" ? `${base}.webp` : src;

  return (
    <img
      src={currentSrc}
      alt={alt}
      {...imgProps}
      onError={(e) => {
        if (stage === "avif") setStage("webp");
        else if (stage === "webp") setStage("original");
        imgProps.onError?.(e);
      }}
    />
  );
}
